<template>
  <tree-select
    ref="select"
    class="zm-select"
    v-if="tree"
    v-bind="$props"
  >
    <template v-slot:prefix>
      <slot name="prefix"></slot>
    </template>
  </tree-select>
  <filterable-select
    ref="select"
    class="zm-select"
    v-else-if="filterable"
    v-bind="[$props,$attrs]"
  >
    <template v-slot:prefix>
      <slot name="prefix"></slot>
    </template>
    <template v-slot:empty>
      <slot name="empty"></slot>
    </template>
    <template v-slot:default>
      <slot></slot>
    </template>
  </filterable-select>
  <normal-select
    v-else
    ref="select"
    class="zm-select"
    v-bind="$props"
  >
    <template v-slot:prefix>
      <slot name="prefix"></slot>
    </template>
    <template v-slot:empty>
      <slot name="empty"></slot>
    </template>
    <template v-slot:default>
      <slot></slot>
    </template>
  </normal-select>
</template>

<script>
  import TreeSelect from './tree-select';
  import FilterableSelect from './filterable-select';
  import NormalSelect from './normal-select';
  import { noop } from '../../utils/util';
  import '../../../i18n/select';
  import { t } from '../../locale';

  export default {
    name: 'ZmSelect',
    components: {
      TreeSelect,
      NormalSelect,
      FilterableSelect
    },
    props: {
      id: {
        type: String,
        default: ''
      },
      value: {
        required: true
      },
      ariaRequired: Boolean,
      size: {
        type: String,
        default: 'small'
      },
      disabled: Boolean,
      clearable: Boolean,
      filterable: Boolean,
      allowCreate: Boolean,
      loading: Boolean,
      hasMore: Boolean,
      remote: Boolean,
      loadingText: String,
      noMatchText: String,
      noDataText: String,
      remoteMethod: Function,
      filterMethod: Function,
      widthLimit: [Boolean, Object],
      multiple: Boolean,
      multipleLimit: {
        type: Number,
        default: 0
      },
      placeholder: {
        type: String,
        default() {
          return t('select.placeholder');
        }
      },
      defaultFirstOption: Boolean,
      valueKey: {
        type: String,
        default: 'value'
      },
      collapseTags: Boolean,
      popperAppendToBody: Boolean,
      inlineLabel: String,
      listLabel: String,
      listLabelBy: String,
      label: String,
      labelBy: String,
      popperOptions: {
        default() {
          return {
            gpuAcceleration: false
          };
        }
      },
      popperClass: String,
      tree: {
        type: Object,
        default() {
          return null;
        }
      },
      checkboxOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptions: {
        type: Array,
        default() {
          return [];
        }
      },
      selectOptionsValueKey: {
        type: String,
        default: 'value'
      },
      limitQueryLength: {
        type: Number,
        default: 0
      },
      filterLabel: Function,
      queryWhenFocus: Boolean,
      queryWhenVisible: {
        type: Boolean,
        default: true
      },
      selectedAfterFocus: Boolean,
      maxLine: {
        type: Number,
        default: 0
      },
      clearQueryWhenVisible: {
        type: Boolean,
        default: true
      },
      queryAfterValueChange: Boolean,
      checkStrictly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        visible: false,
        query: '',
        hoverIndex: -1,
        options: []
      };
    },
    mounted() {
      this.makeProxy();
    },
    watch: {
      filterable() {
        this.$nextTick(this.makeProxy);
      }
    },
    methods: {
      makeProxy() {
        this.proxyEvents();
        this.proxyMethods();
        this.proxyData();
        this.proxyRefs();
      },
      proxyEvents() {
        const events = ['input', 'change', 'visible-change', 'remove-tag', 'focus', 'blur', 'clear'];
        const vm = this.$refs.select;
        events.forEach((event) => {
          vm.$on(event, (...args) => {
            this.$emit.apply(this, [event, ...args]);
          });
        });
      },
      proxyMethods() {
        const methods = ['focus', 'blur', 'handleESC', 'handleQueryChange', 'toggleMenu', 'onScrollEnd'];
        methods.forEach((method) => {
          const vm = this.$refs.select;
          if (vm[method]) {
            this[method] = vm[method].bind(vm);
          } else {
            this[method] = noop;
          }
        });
      },
      proxyData() {
        const vm = this.$refs.select;
        Object.keys(this._data).forEach((key) => {
          vm.$watch(key, (newVal) => {
            this[key] = newVal;
          }, { immediate: true });
        });

        Object.keys(this._data).forEach((key) => {
          this.$watch(key, (newVal) => {
            vm[key] = newVal;
          });
        });
      },
      proxyRefs() {
        const vm = this.$refs.select;
        const refs = ['referenceInner'];
        refs.forEach((ref) => {
          this.$refs[ref] = vm.$refs[ref];
        });
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";
  @import "../../scss/mixins/utils";

  @include b(select) {
    display: inline-block;
    position: relative;
    border: $--input-border;
    border-radius: $--input-border-radius;
    transition: $--border-transition-base;
    background-color: #fff;
    vertical-align: middle;
    box-sizing: border-box;

    &:hover,
    &.is-focus {
      border-color: $--input-focus-border;
      .zm-select__caret {
        color: $--color-border-primary;
      }
      [disabled] + .zm-select-toggle .zm-select__caret {
        color: inherit;
      }
    }
    &.is-disabled {
      cursor: not-allowed;
      background-color: $--disabled-fill-base;
      border-color: $--disabled-border-base;

      .zm-select-input__inner {
        color: $--color-text-disabled;
      }
      .zm-select-span__inner {
        color: $--color-text-disabled;
      }
    }

    .zm-select-input.has-slot {
      display: flex;
      align-items: center;
      .zm-select-input__inner {
        flex: 1;
        width: auto;
      }
      .zm-select-span__inner {
        flex: 1;
        width: auto;
      }
    }
    .zm-select-input {
      position: relative;
      display: block;
      align-items: center;
      overflow-y: auto;
      overflow-x: hidden;
      border: none;
      /*line-height: $--input-height - 2;*/
      font-size: $--font-size-base;

      padding: $--input-default-padding-vertical $--input-default-padding-horizontal;
      padding-right: $--input-default-padding-horizontal + 13;

      .zm-select-prefix {
        display: block;
        line-height: 19px;
      }
    }
    .zm-select-input__inner {
      display: inline-block;
      resize: vertical;
      border: none;
      border-radius: $--input-border-radius;
      vertical-align: middle;
      -ms-overflow-style: none;
      width: 100%;
      line-height: $--input-height - 2;
      height: $--input-height - 2;
      max-width: 100%;
      min-width: 60px;
      padding-top: 0;
      padding-bottom: 0;
      color: $--input-color;
      font-size: $--font-size-base;
      background-color: transparent;
      background-image: none;

      &::placeholder {
        color: $--input-placeholder-color;
      }
      &:focus {
        outline: none;
      }
      &::-ms-clear {
        display: none;
      }
    }
    .zm-select-span__inner {
      display: block;
      vertical-align: middle;
      -ms-overflow-style: none;
      border: none;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      border-radius: $--input-border-radius;
      width: 100%;
      line-height: $--input-height - 2;
      height: $--input-height - 2;
      max-width: 100%;
      min-width: 60px;
      padding-top: 0;
      padding-bottom: 0;
      /*padding-left: 2px;*/
      color: $--input-color;
      background-color: transparent;
      background-image: none;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }

      &:focus {
        outline: none;
      }
      &::-ms-clear {
        display: none;
      }
      &.is-placeholder {
        color: $--color-text-placeholder;
      }
    }
    &.zm-select--xLarge .zm-select--show-label .zm-select-span__inner,
    &.zm-select--xLarge .zm-select--show-label .zm-select-input__inner {
      height: 32px;
      line-height: 32px;
    }
    &.zm-select--xLarge .zm-select--show-label .zm-select__tags{
      min-height: 28px;
      padding-top: 4px;
    }
    .zm-select-input {
      [disabled] + .zm-select-toggle {
        cursor: not-allowed;
        color: $--color-text-disabled;
        .zm-select__caret {
          color: $--color-text-disabled;
        }
        &:focus {
          color: $--color-text-disabled;
          background-color: transparent;
        }
        &:hover .zm-select__caret {
          color: $--color-text-disabled;
        }
      }
      .zm-select-toggle {
        position: absolute;
        right: 8px;
        top: 50%;
        margin-top: -8px;
        padding: 0;
        line-height: 14px;
        border-radius: 50%;
        background-color: transparent;
        border: none;
        .zm-button__slot {
          line-height: 11px;
        }
        &:focus {
          outline: none;
          color: $--color-text-primary-hover;
          background-color: $--color-background-secondary-active;
          .zm-select__caret {
            color: inherit;
          }
        }
        &:hover {
          background-color: transparent;
          border-right-color: transparent;
        }
      }
      .zm-select-clear {
        position: absolute;
        right: 24px;
        top: 50%;
        margin-top: -8px;
        padding: 0;
        line-height: 14px;
        border-radius: 50%;
        background-color: transparent;
        border: none;
      }
      .zm-select__caret {
        text-align: center;
        color: $--select-input-color;
        font-size: $--font-size-base;
        width: 16px;
        height: 16px;
        line-height: 16px;
        transition: transform .3s;
        transform: rotateZ(180deg);

        @include when(reverse) {
          transform: rotateZ(0deg);
        }

        @include when(show-close) {
          font-size: $--select-font-size;
          text-align: center;
          transform: rotateZ(180deg);
          border-radius: $--border-radius-circle;
          color: $--select-input-color;
          transition: $--color-transition-base;

          &:hover {
            color: $--select-close-hover-color;
          }
        }
      }
      &.zm-select--show-label{
        padding-top: 14px;
        line-height: $--input-xlg-height - 20;
      }
      .zm-select__label{
        position: absolute;
        left: 12px;
        top: 4px;
        right: 12px;
        font-size: 12px;
        line-height: 14px;
        margin-bottom: 0;
        color: $--color-text-secondary;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
    }
    .zm-select-scrollbar {
      border-radius: 8px;
    }

    @include e(input) {
      border: none;
      outline: none;
      padding: 0;
      margin-left: 6px;
      color: $--select-multiple-input-color;
      font-size: $--select-font-size;
      appearance: none;
      height: 30px;
      min-width: 20px;
      background-color: transparent;
    }

    @include e(close) {
      cursor: pointer;
      position: absolute;
      top: 8px;
      z-index: $--index-top;
      right: 25px;
      color: $--select-input-color;
      line-height: 18px;
      font-size: $--select-input-font-size;

      &:hover {
        color: $--select-close-hover-color;
      }
    }

    @include e(tags) {
      // position: absolute;
      // z-index: $--index-normal;
      // top: 50%;
      // transform: translateY(-50%);
      display: flex;
      line-height: normal;
      white-space: normal;
      align-items: center;
      flex-wrap: wrap;
      margin-left: -6px;
      & > span {
        display: contents;
      }
    }

    @include e(loading) {
      height: 33px;
      margin-top: 8px;
      z-index: -1;

      .zm-loading-spinner {
        .circular {
          height: 30px;
          width: 30px;
        }
      }
    }

    .zm-tag {
      box-sizing: border-box;
      border-color: transparent;
      margin: 3px 0 3px 6px;
      background-color: #f0f2f5;
      max-width: 200px;
      &__close.zm-icon-close {
        background-color: $--color-text-placeholder;
        right: -7px;
        top: 0;
        color: $--color-white;

        &:hover {
          background-color: $--color-text-secondary;
        }

        &::before {
          display: block;
          transform: translate(0, .5px);
        }
      }

      &.zm-tag-fix {
        margin: 3px 0 3px 6px;
      }

      &.is-hit {
        border-color: $--tag-info-color;
      }
    }

    @include m(small) {
      .zm-select-input {
        .zm-select__caret {
          font-size: $--font-size-small;
        }
      }
    }

    @include m(large) {
      .zm-select-input__inner {
        line-height: $--button-lg-height - 2;
        height: $--button-lg-height - 2;
      }
      .zm-select-span__inner {
        line-height: $--button-lg-height - 2;
        height: $--button-lg-height - 2;
      }
    }
    @include m(xLarge) {
      .zm-select-input__inner,
      .zm-select-span__inner{
        line-height: $--input-xlg-height - 2;
        height: $--input-xlg-height - 2;
        font-size: 16px;
      }
      .zm-select--show-label {
        .zm-select__tags {
          .zm-tag{
            margin-top: 2px;
            margin-bottom: 2px;
          }
        }
      }
    }
  }
  @media (forced-colors: active) {
    .is-keyboard-event .zm-select.is-focus {
      outline: #0000FF dotted thin;
      outline-offset: -6px;
    }
  }
</style>
